import { Button, Card, Divider, Tooltip } from '@mui/material';
import { Link as BrowserLink } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '../../../../components/molecules/page-container';
import { PageHeader } from '../../../../components/molecules/page-header';
import { Plus as PlusIcon } from '../../../../icons/plus';
import { useDestinations } from '../../hooks/query/use-destinations';
import { DestinationActionSlotPayload, DestinationsTable } from '../organisms/destinations-table';
import AssignAcpToDestinationDialog from '../organisms/dialogs/assign-access-control-profiles-to-destination-dialog';
import RemoveDestinationDialog from '../organisms/dialogs/remove-destination-dialog';

import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import { Pagination } from '@/components/organisms/pagination';
import { useDialog } from '@/hooks/use-dialog';
import { usePagination } from '@/hooks/use-pagination';
import { Can, usePermissions, withPermissions } from '@/permissions/can';

const Destinations = () => {
  const { t } = useTranslation(['destinations', 'common']);

  const permissions = usePermissions();

  const [
    isOpenAssignAccessControlProfilesToDestinationDialog,
    handleOpenAssignAccessControlProfilesToDestinationDialog,
    handleCloseAssignAccessControlProfilesToDestinationDialog,
  ] = useDialog();

  const [
    isDeleteDestinationDialogOpen,
    handleOpenDeleteDestinationDialog,
    handleCloseDeleteDestinationDialog,
  ] = useDialog();

  const { page, perPage, changePage, changePerPage, setTotalItems, totalPages } = usePagination();

  const { data, isLoading, isFetching, error } = useDestinations({ page, perPage });

  const [selectedDestination, setSelectedDestination] =
    useState<DestinationActionSlotPayload | null>(null);

  const actionsSlot = useCallback(
    ({
      id,
      accessControlProfilesIds,
      name,
      destinationHasAvailableKiosks,
    }: DestinationActionSlotPayload) => {
      const actions = [
        {
          key: 'manage-access',
          label: t('destinations:table.setAccess'),
          onClick: () => {
            setSelectedDestination({
              id,
              accessControlProfilesIds,
              name,
              destinationHasAvailableKiosks,
            });
            handleOpenAssignAccessControlProfilesToDestinationDialog();
          },
          disabled: permissions.cannot('manageAccessControlProfiles', 'Destinations'),
          tooltip: t('common:notSufficientPermissions'),
        },
        {
          key: 'remove',
          label: t('common:button.delete'),
          onClick: () => {
            setSelectedDestination({
              id,
              accessControlProfilesIds,
              name,
              destinationHasAvailableKiosks,
            });
            handleOpenDeleteDestinationDialog();
          },
          disabled: permissions.cannot('delete', 'Destinations'),
          tooltip: t('common:notSufficientPermissions'),
        },
      ];

      return <ActionsTableMenu actions={actions} />;
    },
    [],
  );

  useEffect(() => {
    if (data) {
      setTotalItems(data?.total);
    }
  }, [data]);

  return (
    <PageContainer helmetPageTitle={t('destinations:tab')}>
      <PageHeader
        title={t('destinations:title')}
        extras={
          <Can I="create" a="Destinations" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    color="primary"
                    component={BrowserLink}
                    to="/destinations/create"
                    variant="contained"
                    size="large"
                    startIcon={<PlusIcon fontSize="small" />}
                    disabled={!allowed}>
                    {t('destinations:create')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
        <DestinationsTable
          data={data?.resources || []}
          error={error?.response?.data?.message || ''}
          isLoading={isLoading}
          actionsSlot={actionsSlot}
        />
        <Divider sx={{ mt: 'auto' }} />
        <Pagination
          changePage={changePage}
          changePerPage={changePerPage}
          isFetching={isFetching}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
        />
      </Card>
      <AssignAcpToDestinationDialog
        open={isOpenAssignAccessControlProfilesToDestinationDialog}
        onCancel={handleCloseAssignAccessControlProfilesToDestinationDialog}
        destinationId={selectedDestination?.id || 0}
        accessControlProfilesIds={selectedDestination?.accessControlProfilesIds || []}
      />
      <RemoveDestinationDialog
        open={isDeleteDestinationDialogOpen}
        onCancel={handleCloseDeleteDestinationDialog}
        destinationId={selectedDestination?.id || 0}
        destinationName={selectedDestination?.name || ''}
        destinationHasAvailableKiosks={selectedDestination?.destinationHasAvailableKiosks || false}
      />
    </PageContainer>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Destinations'),
  Destinations,
);
